import { Currency } from '@luminovo/commons';
import { z } from 'zod/v4';

import {
    AssemblyIndustryRuntype,
    NonPcbAssemblyTypeEnumRuntype,
    PcbAssemblyTypeEnumRuntype,
} from '../assembly/assemblyBackendTypes';
import { endpoint } from '../http/endpoint';
import { SourcingTemplateLeadTimePreferencesRuntype } from '../sourcingTemplate/sourcingTemplateBackendTypes';

// "New" allows all 5 public assembly types; "Existing" excludes PcbOnly per the backend.
export const NewAssemblyTypeRuntype = PcbAssemblyTypeEnumRuntype.or(NonPcbAssemblyTypeEnumRuntype.exclude(['BOM']));
export const DuplicateAssemblyTypeRuntype = z.enum(['Box', 'Pcba', 'Cable', 'System']);

export const VolumeEstimateInputRuntype = z.object({
    currency: z.enum(Currency),
    // Rust `Decimal` serialises to JSON as a string.
    lower: z.string().nullable(),
    upper: z.string().nullable(),
});

export const ProjectAssemblyInputRuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('New'),
        key: z.string(),
        name: z.string(),
        assembly_type: NewAssemblyTypeRuntype,
    }),
    z.object({
        type: z.literal('Existing'),
        key: z.string(),
        assembly_id: z.string(),
        assembly_type: DuplicateAssemblyTypeRuntype,
    }),
]);

export const ProjectDemandGroupInputRuntype = z.object({
    name: z.string(),
    description: z.string().nullable(),
    delivery_start_date: z.string().nullable(),
    delivery_end_date: z.string().nullable(),
    sourcing_scenarios: z.array(
        z.object({
            sourcing_template_id: z.string(),
            name: z.string(),
            lead_time_preference: SourcingTemplateLeadTimePreferencesRuntype,
        }),
    ),
    assembly_quantities: z.array(z.object({ key: z.string(), quantity: z.number().int().positive() })),
    ship_to_site_id: z.string().nullable(),
});

export const ProjectInputDTORuntype = z.object({
    general: z.object({
        name: z.string(),
        internal_number: z.string().nullable(),
        industry: AssemblyIndustryRuntype,
        customer: z.string(),
        due_date: z.string().nullable(),
        contributors: z.object({
            external_contributors: z.array(z.string()),
            internal_contributors: z.array(z.string()),
        }),
        volume_estimate: VolumeEstimateInputRuntype.nullable(),
    }),
    assemblies: z.array(ProjectAssemblyInputRuntype),
    demand_groups: z.array(ProjectDemandGroupInputRuntype),
});

export type ProjectInputDTO = z.infer<typeof ProjectInputDTORuntype>;

export const ProjectCreationResponseRuntype = z.object({ id: z.string() });

export const projectEndpoints = {
    'POST /projects': endpoint({
        description:
            'Creates a project together with its assemblies (new or referenced from existing) and demand groups in a single request. Returns the new project id.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: ProjectInputDTORuntype,
        // Backend returns the bare object, not wrapped in `{ data: ... }`.
        responseBody: ProjectCreationResponseRuntype,
        invalidates: [
            'GET /rfqs',
            'GET /organization-settings/auto-rfq-number/next',
            // Both variants can add assembly rows (`New` always does).
            'POST /assemblies/paginated',
        ],
        // Project creation succeeds RFQ creation, and the backend gates both on `CanCreateRfq`.
        actionPermission: () => 'CanCreateRfq',
    }),
};
